import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Bell, Bug, Fingerprint, KeyRound, LogOut, User } from 'lucide-react';
import { cx } from '../charte';
import { Photo } from '../Photos';
import type { PushState } from '../../api/push';
import type { Tab } from './nav';

/** Menu déroulant de l'en-tête : se ferme au clic à côté et à Échap ; pleine largeur sur téléphone. */
function usePopover() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return { open, setOpen, ref };
}

const panel =
  'z-50 overflow-hidden rounded-[20px] border border-line bg-white shadow-pop animate-pop ' +
  'fixed inset-x-3 top-[calc(env(safe-area-inset-top)+64px)] sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2';

export interface Notice {
  id: string;
  icon: ReactNode;
  title: string;
  desc: string;
  /** Onglet ouvert au clic sur la notification. */
  tab?: Tab;
}

export function NotificationsMenu({
  notifications,
  readIds,
  onRead,
  onReadAll,
  onTab,
  pushState,
  pushBusy,
  pushGranted,
  onTogglePush,
}: {
  notifications: Notice[];
  readIds: string[];
  onRead: (id: string) => void;
  onReadAll: () => void;
  onTab: (t: Tab) => void;
  pushState: PushState;
  pushBusy: boolean;
  pushGranted: boolean;
  onTogglePush: () => void;
}) {
  const { open, setOpen, ref } = usePopover();
  const unread = notifications.filter((n) => !readIds.includes(n.id)).length;
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        aria-expanded={open}
        className="relative grid h-10 w-10 place-items-center rounded-pill text-secondary transition-colors hover:bg-surface-2 hover:text-body"
      >
        <Bell size={20} aria-hidden />
        {unread > 0 && (
          <span className="absolute right-1 top-1 grid h-4 min-w-[16px] place-items-center rounded-pill bg-danger px-1 text-[10px] font-bold text-white">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div className={cx(panel, 'sm:w-[360px]')}>
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
            <h3 className="text-[14px] font-semibold text-heading">Notifications</h3>
            {unread > 0 && (
              <button type="button" onClick={onReadAll} className="text-xs font-medium text-primary-600 hover:text-primary-700">
                Tout marquer comme lu
              </button>
            )}
          </div>

          {/* Invitation à activer les notifications du téléphone */}
          {pushState !== 'unsupported' && pushState !== 'subscribed' && !pushGranted && (
            <div className="border-b border-slate-100 bg-surface-indigo px-4 py-3">
              {pushState === 'denied' ? (
                <p className="text-xs leading-relaxed text-secondary">
                  Notifications bloquées. Autorisez-les pour Permio dans les réglages de votre navigateur ou de votre téléphone.
                </p>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <p className="flex-1 text-xs text-body">Activez les notifications pour être prévenu·e.</p>
                  <button
                    type="button"
                    onClick={onTogglePush}
                    disabled={pushBusy}
                    className="shrink-0 rounded-pill bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
                  >
                    {pushBusy ? '…' : 'Activer'}
                  </button>
                </div>
              )}
            </div>
          )}

          {notifications.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-secondary">Aucune notification pour le moment.</p>
          ) : (
            <ul className="max-h-[60dvh] overflow-y-auto p-1.5">
              {notifications.map((n) => {
                const isUnread = !readIds.includes(n.id);
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => {
                        onRead(n.id);
                        if (n.tab) {
                          onTab(n.tab);
                          setOpen(false);
                        }
                      }}
                      className="flex w-full items-start gap-3 rounded-row px-3 py-2.5 text-left transition-colors hover:bg-surface-light"
                    >
                      <span className="relative grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-surface-indigo">
                        {n.icon}
                        {isUnread && <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-primary" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className={cx('block text-sm', isUnread ? 'font-semibold text-heading' : 'font-medium text-body')}>{n.title}</span>
                        <span className="block text-xs leading-relaxed text-secondary">{n.desc}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function MenuRow({
  icon: Icon,
  label,
  status,
  statusTone = 'text-primary-600',
  danger = false,
  disabled,
  onClick,
}: {
  icon: typeof User;
  label: string;
  status?: string;
  statusTone?: string;
  danger?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cx(
        'flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition-colors disabled:opacity-60',
        danger ? 'text-danger-dark hover:bg-danger-light' : 'text-body hover:bg-surface-light',
      )}
    >
      <span className="flex items-center gap-2.5">
        <Icon size={16} className={danger ? '' : 'text-muted'} aria-hidden />
        {label}
      </span>
      {status && <span className={cx('text-xs font-semibold', statusTone)}>{status}</span>}
    </button>
  );
}

export function AccountMenu({
  fullName,
  email,
  monogram,
  photo,
  pushState,
  pushBusy,
  onTogglePush,
  bioSupported,
  bioRegistered,
  bioBusy,
  onEnableBio,
  hasPassword,
  onProfile,
  onPassword,
  onSupport,
  onLogout,
}: {
  fullName: string;
  email?: string | null;
  monogram: string;
  photo: string | null | undefined;
  pushState: PushState;
  pushBusy: boolean;
  onTogglePush: () => void;
  bioSupported: boolean;
  bioRegistered: boolean;
  bioBusy: boolean;
  onEnableBio: () => void;
  hasPassword: boolean;
  onProfile: () => void;
  onPassword: () => void;
  onSupport: () => void;
  onLogout: () => void;
}) {
  const { open, setOpen, ref } = usePopover();
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Mon compte"
        aria-expanded={open}
        className="relative grid h-9 w-9 place-items-center overflow-hidden rounded-full bg-avatar text-xs font-semibold text-primary-700 transition-shadow hover:ring-4 hover:ring-primary-50"
      >
        {monogram}
        <Photo url={photo} />
      </button>
      {open && (
        <div className={cx(panel, 'sm:w-64')}>
          <div className="border-b border-slate-100 px-4 py-3">
            <div className="truncate text-sm font-semibold text-heading">{fullName}</div>
            {email && <div className="truncate text-xs text-secondary">{email}</div>}
          </div>
          <div className="p-1.5">
            <MenuRow
              icon={User}
              label="Mon profil"
              onClick={() => {
                setOpen(false);
                onProfile();
              }}
            />
            {pushState !== 'unsupported' && (
              <MenuRow
                icon={Bell}
                label="Notifications"
                onClick={onTogglePush}
                disabled={pushBusy || pushState === 'denied'}
                status={pushState === 'subscribed' ? 'Activées' : pushState === 'denied' ? 'Bloquées' : 'Activer'}
                statusTone={pushState === 'subscribed' ? 'text-success-dark' : pushState === 'denied' ? 'text-danger' : 'text-primary-600'}
              />
            )}
            {bioSupported && (
              <MenuRow
                icon={Fingerprint}
                label="FaceID / Touch ID"
                onClick={onEnableBio}
                disabled={bioBusy || bioRegistered}
                status={bioRegistered ? 'Activé' : 'Activer'}
                statusTone={bioRegistered ? 'text-success-dark' : 'text-primary-600'}
              />
            )}
            <MenuRow
              icon={KeyRound}
              label="Mot de passe"
              status={hasPassword ? 'Changer' : 'Définir'}
              onClick={() => {
                setOpen(false);
                onPassword();
              }}
            />
            <MenuRow
              icon={Bug}
              label="Signaler un problème"
              onClick={() => {
                setOpen(false);
                onSupport();
              }}
            />
            <div className="my-1 h-px bg-slate-100" />
            <MenuRow icon={LogOut} label="Se déconnecter" danger onClick={onLogout} />
          </div>
        </div>
      )}
    </div>
  );
}
